/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { formatCostCenterFinderOptionLabel } from '@gh/common-internal-shared';
import { EnumDictionary, getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  isJeCcFiltered,
  isJeComponentCostCenter,
  JeCcFilter,
  JeCostCenter,
  getJeCostCenterId,
  JeItemType,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  JeDetailsState,
  queryJeCcFilter,
  queryJeCostCenters,
  queryJeEditable,
  queryJeSelectedCostCenterId,
  queryJeCostCenterItems,
  queryJeCostCenterOrders,
} from '../je-details.state';
import { JeDetailsCcGridDefinition } from './je-details-cc-grid-definition.service';
import { NotificationService } from '@gh/core-ui';
import { isEmpty } from 'lodash';

@Component({
  standalone: false,
  selector: 'gh-je-details-cc-list',
  templateUrl: 'je-details-cc-list.component.html',
  styleUrls: ['je-details-cc-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JeDetailsCcGridDefinition],
})
export class JeDetailsCcListComponent implements OnInit {
  readonly costCenterGridFinderDisplayWith = formatCostCenterFinderOptionLabel;

  editable$: Observable<boolean>;
  costCenters$: Observable<JeCostCenter[]>;
  selectedCostCenterId$: Observable<Maybe<ID>>;
  ccFilter$: Observable<JeCcFilter>;

  ccFilterDict: EnumDictionary<JeCcFilter>;

  constructor(private route: ActivatedRoute,
              public gridDefinition: JeDetailsCcGridDefinition,
              private state: State<JeDetailsState>,
              private commands: JeCommandService,
              private notifications: NotificationService) {

  }

  ngOnInit(): void {
    const { ccFilterDict } = getRouteInitData(this.route);

    this.ccFilter$ = this.state.select(queryJeCcFilter);
    this.costCenters$ = this.ccFilter$.pipe(switchMap$((filter) => this.state.select(queryJeCostCenters).pipe(
      map$((costCenters) => costCenters.filter((cc) => isJeCcFiltered(cc, filter))))));
    this.selectedCostCenterId$ = this.state.select(queryJeSelectedCostCenterId);
    this.editable$ = this.state.select(queryJeEditable);

    this.ccFilterDict = ccFilterDict;
  }

  onAdd(): void {
    this.commands.costCenterAdd();
  }

  onViewOrderDetails(costCenter: JeCostCenter): void {
    this.commands.costCenterViewOrderDetails(costCenter);
  }

  isTypicalCc(costCenter: JeCostCenter): boolean {
    return !isJeComponentCostCenter(costCenter);
  }

  onSelectedCostCenterChanged(id?: ID): void {
    this.commands.costCenterSelect(id);
  }

  onChangeCcFilter(filter: JeCcFilter): void {
    this.commands.setCostCenterFilter(filter);
  }
  onDelete(costCenter: JeCostCenter): void {
    const costCenterId = getJeCostCenterId(costCenter);
    const items = this.state.project(queryJeCostCenterItems, costCenterId);   
    const order = this.state.project(queryJeCostCenterOrders, costCenterId);
    
    if (!isEmpty(items)) {
      const hasComponentItems = items.some(item => item.type === JeItemType.Component);
      if (hasComponentItems) {
        this.notifications.warning('je_cost_center_remove_warning');
        return;
      }    
    }
    this.commands.costCenterRemove(costCenter, items, order);
  }

  canBeRemoved(costCenter: JeCostCenter): boolean {
    if (costCenter.cost !== 0) {
      return false;
    }
    
    const costCenterId = getJeCostCenterId(costCenter);
    const orders = this.state.project(queryJeCostCenterOrders, costCenterId);
    if (!isEmpty(orders)) {
      const hasOrderWithAttachments = orders.some(order => 
        !isEmpty(order.attachments)
      );
      
      if (hasOrderWithAttachments) {
        return false;
      }
    }
    
    return true;
  }
}
